// src/components/admin/settings/CategoriesSettings.jsx
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FaGripVertical, FaUpload, FaTrash } from 'react-icons/fa';
import './SettingsPages.css';

let idCounter = 0;
const nextId = () => `cat-${Date.now()}-${idCounter++}`;

const CategoriesSettings = () => {
  const [categories, setCategories] = useState([]);
  const [categoryLabels, setCategoryLabels] = useState({});
  const [categoryImages, setCategoryImages] = useState({}); // { [categorySlug]: imageUrl }
  const [categoryIds, setCategoryIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null); // tracks which row is uploading

  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await fetch('https://demo-site.storii.com.pk/demo-api/public/get-settings.php');
      const data = await response.json();
      if (data.success && data.settings) {
        let cats = [];
        let labels = {};
        try {
          cats = data.settings.categories ? JSON.parse(data.settings.categories) : ['electronics', 'clothing', 'books', 'accessories', 'home', 'sports'];
        } catch (e) {
          cats = ['electronics', 'clothing', 'books', 'accessories', 'home', 'sports'];
        }
        try {
          labels = data.settings.category_labels ? JSON.parse(data.settings.category_labels) : {};
        } catch (e) {
          labels = {};
        }

        // Images aren't stored as one JSON blob — each is its own
        // `category_image_{slug}` key, matching what's already in your
        // store_settings table (category_image_clothing, etc.)
        const images = {};
        cats.forEach(cat => {
          const key = `category_image_${cat}`;
          if (data.settings[key]) images[cat] = data.settings[key];
        });

        setCategories(cats);
        setCategoryLabels(labels);
        setCategoryImages(images);
        setCategoryIds(cats.map(() => nextId()));
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateCategories = (newCategories, newLabels) => {
    setCategories(newCategories);
    setCategoryLabels(newLabels);
  };

  const handleImageUpload = async (e, cat) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image size should be less than 2MB');
      return;
    }

    const formData = new FormData();
    formData.append('image', file);
    setUploadingKey(cat);

    try {
      const response = await fetch('https://demo-site.storii.com.pk/demo-api/admin/upload-category-image.php', {
        method: 'POST',
        body: formData
      });
      const data = await response.json();
      if (data.success && data.image_url) {
        setCategoryImages(prev => ({ ...prev, [cat]: data.image_url }));
        toast.success('Category image uploaded!');
      } else {
        toast.error(data.message || 'Upload failed');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setUploadingKey(null);
      e.target.value = '';
    }
  };

  const removeImage = (cat) => {
    setCategoryImages(prev => {
      const copy = { ...prev };
      delete copy[cat];
      return copy;
    });
  };

  // --- Drag-and-drop reordering ---
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (index !== dragOverIndex) setDragOverIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      handleDragEnd();
      return;
    }

    const reorder = (arr) => {
      const copy = [...arr];
      const [moved] = copy.splice(draggedIndex, 1);
      copy.splice(dropIndex, 0, moved);
      return copy;
    };

    setCategories(reorder(categories));
    setCategoryIds(reorder(categoryIds));
    handleDragEnd();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Flatten categoryImages into individual category_image_{slug} keys,
      // matching the naming pattern already used elsewhere in store_settings.
      const imagePayload = {};
      categories.forEach(cat => {
        if (categoryImages[cat]) {
          imagePayload[`category_image_${cat}`] = categoryImages[cat];
        }
      });

      const response = await fetch('https://demo-site.storii.com.pk/demo-api/admin/update-settings-simple.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          categories: JSON.stringify(categories),
          category_labels: JSON.stringify(categoryLabels),
          ...imagePayload
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Categories saved successfully!');
      } else {
        toast.error('Failed to save');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loader">Loading categories...</div>;

  return (
    <div className="settings-page">
      <h2>Product Categories</h2>
      <p className="section-description">Manage categories for product filtering. Drag to reorder how they appear on the storefront.</p>

      <form onSubmit={handleSubmit} className="settings-form">
        <div className="categories-list">
          <h3>Current Categories</h3>
          {categories.map((cat, index) => (
            <div
              key={categoryIds[index]}
              className={`category-item cat-drag-item ${draggedIndex === index ? 'cat-drag-dragging' : ''} ${dragOverIndex === index && draggedIndex !== index ? 'cat-drag-over' : ''}`}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
            >
              <span className="cat-drag-handle" title="Drag to reorder">
                <FaGripVertical />
              </span>

              {/* Category image thumbnail + upload */}
              <div className="cat-image-cell">
                {categoryImages[cat] ? (
                  <div className="cat-image-preview">
                    <img src={categoryImages[cat]} alt={cat} />
                    <button
                      type="button"
                      className="cat-image-remove-btn"
                      onClick={() => removeImage(cat)}
                      title="Remove image"
                    >
                      <FaTrash />
                    </button>
                  </div>
                ) : (
                  <label className="cat-image-upload-btn">
                    <FaUpload />
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={(e) => handleImageUpload(e, cat)}
                      disabled={uploadingKey === cat}
                    />
                  </label>
                )}
                {uploadingKey === cat && <span className="cat-image-uploading">...</span>}
              </div>

              <input
                type="text"
                className="category-key-input"
                value={cat}
                onChange={(e) => {
                  const newCategories = [...categories];
                  const newLabels = { ...categoryLabels };
                  const oldKey = cat;
                  const newKey = e.target.value.toLowerCase().replace(/\s+/g, '_');
                  newCategories[index] = newKey;
                  if (newLabels[oldKey]) {
                    newLabels[newKey] = newLabels[oldKey];
                    delete newLabels[oldKey];
                  }
                  // Carry the image over to the new key too, so renaming
                  // a category doesn't orphan its uploaded image.
                  if (categoryImages[oldKey]) {
                    setCategoryImages(prev => {
                      const copy = { ...prev };
                      copy[newKey] = copy[oldKey];
                      delete copy[oldKey];
                      return copy;
                    });
                  }
                  updateCategories(newCategories, newLabels);
                }}
                placeholder="Category key"
              />
              <input
                type="text"
                className="category-label-input"
                value={categoryLabels[cat] || cat}
                onChange={(e) => {
                  const newLabels = { ...categoryLabels };
                  newLabels[cat] = e.target.value;
                  setCategoryLabels(newLabels);
                }}
                placeholder="Display name"
              />
              <button
                type="button"
                className="remove-category-btn"
                onClick={() => {
                  const newCategories = categories.filter((_, i) => i !== index);
                  const newLabels = { ...categoryLabels };
                  delete newLabels[cat];
                  setCategoryIds(categoryIds.filter((_, i) => i !== index));
                  removeImage(cat);
                  updateCategories(newCategories, newLabels);
                }}
              >
                ✕
              </button>
            </div>
          ))}
        </div>

        <div className="add-category">
          <button
            type="button"
            className="add-category-btn"
            onClick={() => {
              const newKey = 'new_category_' + (categories.length + 1);
              setCategoryIds([...categoryIds, nextId()]);
              updateCategories([...categories, newKey], { ...categoryLabels, [newKey]: 'New Category' });
            }}
          >
            + Add New Category
          </button>
        </div>

        <button type="submit" disabled={saving} className="save-btn">{saving ? 'Saving...' : 'Save Categories'}</button>
      </form>
    </div>
  );
};

export default CategoriesSettings;